/**
 * Real-Time Data Synchronization Service
 *
 * This service provides real-time Firebase listeners for critical data collections.
 * It integrates with the existing cache system to provide live updates while
 * maintaining performance and preventing memory leaks.
 */

import {
  collection,
  doc,
  query,
  where,
  onSnapshot,
  orderBy,
  limit
} from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import cacheManager, { CACHE_KEYS } from '../utils/cacheManager';

class RealtimeDataService {
  constructor() {
    this.listeners = new Map();
    this.subscriptions = new Set();
    this.isInitialized = false;
    this.currentUser = null;
    this.userRole = null;
    this.userPermissions = null;
  }

  /**
   * Initialize the real-time service with user context
   */
  initialize(user, role, permissions) {
    if (this.isInitialized) {
      console.log('Real-time service already initialized');
      return;
    }

    this.currentUser = user;
    this.userRole = role;
    this.userPermissions = permissions;
    this.isInitialized = true;

    console.log('🔴 Real-time data service initialized for user:', user?.uid);

    // Set up auth state listener for user changes
    this.setupAuthListener();
  }

  /**
   * Set up authentication state listener
   */
  setupAuthListener() {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) {
        // User logged out, clean up all listeners
        this.cleanup();
      } else if (user.uid !== this.currentUser?.uid) {
        // User changed, reinitialize
        console.log('User changed, reinitializing real-time service');
        this.cleanup();
        // Note: Re-initialization should be handled by the parent component
      }
    });

    this.subscriptions.add(unsubscribe);
  }

  /**
   * Subscribe to real-time updates for a specific data type
   */
  subscribe(dataType, options = {}) {
    if (!this.isInitialized || !this.currentUser) {
      console.warn('Real-time service not initialized or no current user');
      return null;
    }

    const {
      collectionName,
      userFilter = true,
      orderBy: orderField = null,
      limit: maxLimit = null,
      onUpdate = null,
      cacheKey = null,
      refreshInterval = null
    } = options;

    if (!collectionName) {
      console.error('Collection name required for real-time subscription');
      return null;
    }

    const subscriptionKey = `${dataType}_${this.currentUser.uid}`;

    // Check if already subscribed
    if (this.listeners.has(subscriptionKey)) {
      console.log(`Already subscribed to ${dataType}`);
      return this.listeners.get(subscriptionKey).unsubscribe;
    }

    console.log(`🔴 Subscribing to real-time ${dataType} updates`);

    try {
      // Build query based on options
      let queryRef = collection(db, collectionName);

      // Add user-specific filtering for user-owned data
      if (userFilter && this.currentUser) {
        if (dataType === 'properties' || dataType === 'tenants' || dataType === 'payments') {
          // For these collections, filter by landlord/agent
          queryRef = query(queryRef, where('landlordId', '==', this.currentUser.uid));
        } else if (dataType === 'maintenance') {
          // Maintenance requests might be filtered differently
          queryRef = query(queryRef, where('assignedTo', '==', this.currentUser.uid));
        }
      }

      // Add ordering if specified
      if (orderField) {
        queryRef = query(queryRef, orderBy(orderField, 'desc'));
      }

      // Add limit if specified
      if (maxLimit) {
        queryRef = query(queryRef, limit(maxLimit));
      }

      // Set up the real-time listener
      const unsubscribe = onSnapshot(
        queryRef,
        (snapshot) => {
          const data = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            _lastUpdated: Date.now()
          }));

          console.log(`🔴 Real-time ${dataType} update: ${data.length} items`);

          // Update cache with fresh data
          if (cacheKey) {
            cacheManager.set(cacheKey, data, this.getCacheTTL(dataType));
          }

          // Call custom update handler if provided
          if (onUpdate) {
            onUpdate(data, { source: 'realtime', timestamp: Date.now() });
          }

          // Emit custom event for other components to listen to
          window.dispatchEvent(new CustomEvent(`realtime-${dataType}-update`, {
            detail: { data, timestamp: Date.now(), source: 'realtime' }
          }));
        },
        (error) => {
          console.error(`❌ Real-time ${dataType} listener error:`, error);

          // Fallback to manual refresh if real-time fails
          if (cacheKey && refreshInterval) {
            setTimeout(() => {
              this.triggerManualRefresh(dataType, cacheKey);
            }, refreshInterval);
          }
        }
      );

      // Store the listener for cleanup
      this.listeners.set(subscriptionKey, {
        unsubscribe,
        dataType,
        options,
        lastUpdate: Date.now()
      });

      return unsubscribe;

    } catch (error) {
      console.error(`❌ Failed to subscribe to ${dataType}:`, error);
      return null;
    }
  }

  /**
   * Subscribe to all critical data types for the current user
   */
  subscribeToAllCriticalData() {
    if (!this.isInitialized) return;

    const subscriptions = [];

    // Properties - user's own properties
    subscriptions.push(this.subscribe('properties', {
      collectionName: 'properties',
      cacheKey: CACHE_KEYS.PROPERTIES_ALL(this.currentUser.uid),
      refreshInterval: 30000 // 30 seconds fallback
    }));

    // Tenants - tenants of user's properties
    subscriptions.push(this.subscribe('tenants', {
      collectionName: 'tenants',
      cacheKey: CACHE_KEYS.TENANTS_ALL(this.currentUser.uid),
      refreshInterval: 30000
    }));

    // Maintenance - requests assigned to user
    subscriptions.push(this.subscribe('maintenance', {
      collectionName: 'maintenance',
      cacheKey: CACHE_KEYS.MAINTENANCE_ALL(this.currentUser.uid),
      refreshInterval: 15000 // 15 seconds for urgent updates
    }));

    // Payments - payments for user's properties
    subscriptions.push(this.subscribe('payments', {
      collectionName: 'payments',
      cacheKey: CACHE_KEYS.PAYMENTS_ALL(this.currentUser.uid),
      refreshInterval: 30000
    }));

    // User role/permissions changes (global for current user)
    subscriptions.push(this.subscribeToUserPermissions());

    return subscriptions.filter(sub => sub !== null);
  }

  /**
   * Subscribe to user permissions changes
   */
  subscribeToUserPermissions() {
    if (!this.currentUser) return null;

    const subscriptionKey = `user_permissions_${this.currentUser.uid}`;

    if (this.listeners.has(subscriptionKey)) {
      return this.listeners.get(subscriptionKey).unsubscribe;
    }

    console.log('🔴 Subscribing to user permissions updates');

    const userDocRef = doc(db, 'users', this.currentUser.uid);

    const unsubscribe = onSnapshot(
      userDocRef,
      (doc) => {
        if (doc.exists()) {
          const userData = doc.data();

          // Check if role or permissions changed
          if (userData.role !== this.userRole) {
            console.log('🔴 User role changed:', this.userRole, '→', userData.role);

            // Update local state
            this.userRole = userData.role;

            // Emit event for components to handle role changes
            window.dispatchEvent(new CustomEvent('user-role-changed', {
              detail: { oldRole: this.userRole, newRole: userData.role }
            }));
          }

          // Update permissions cache
          cacheManager.set(CACHE_KEYS.USER_ROLE(this.currentUser.uid), userData.role, 60 * 60 * 1000);
        }
      },
      (error) => {
        console.error('❌ User permissions listener error:', error);
      }
    );

    this.listeners.set(subscriptionKey, {
      unsubscribe,
      dataType: 'user_permissions',
      lastUpdate: Date.now()
    });

    return unsubscribe;
  }

  /**
   * Get appropriate cache TTL for data type
   */
  getCacheTTL(dataType) {
    const ttlMap = {
      'maintenance': 15 * 60 * 1000, // 15 minutes - urgent updates
      'payments': 30 * 60 * 1000,   // 30 minutes - financial data
      'properties': 60 * 60 * 1000,  // 1 hour - property data
      'tenants': 60 * 60 * 1000,    // 1 hour - tenant data
      'default': 30 * 60 * 1000      // 30 minutes default
    };

    return ttlMap[dataType] || ttlMap.default;
  }

  /**
   * Trigger manual refresh as fallback when real-time fails
   */
  async triggerManualRefresh(dataType, cacheKey) {
    try {
      console.log(`🔄 Triggering manual refresh for ${dataType}`);

      // Import the appropriate service function
      let fetchFunction;
      switch (dataType) {
        case 'properties':
          const { getAllProperties } = await import('../firebase/propertyService');
          fetchFunction = () => getAllProperties(this.currentUser.uid);
          break;
        case 'tenants':
          const { getAllTenants } = await import('../firebase/tenantService');
          fetchFunction = () => getAllTenants(this.currentUser.uid);
          break;
        case 'maintenance':
          const { getAllMaintenance } = await import('../firebase/maintenanceService');
          fetchFunction = () => getAllMaintenance(this.currentUser.uid);
          break;
        case 'payments':
          const { getAllPayments } = await import('../firebase/paymentService');
          fetchFunction = () => getAllPayments(this.currentUser.uid);
          break;
        default:
          console.warn(`No fetch function available for ${dataType}`);
          return;
      }

      const data = await fetchFunction();
      cacheManager.set(cacheKey, data, this.getCacheTTL(dataType));

      console.log(`✅ Manual refresh completed for ${dataType}`);
    } catch (error) {
      console.error(`❌ Manual refresh failed for ${dataType}:`, error);
    }
  }

  /**
   * Unsubscribe from specific data type
   */
  unsubscribe(dataType) {
    const subscriptionKey = `${dataType}_${this.currentUser?.uid}`;

    if (this.listeners.has(subscriptionKey)) {
      const { unsubscribe } = this.listeners.get(subscriptionKey);
      unsubscribe();
      this.listeners.delete(subscriptionKey);
      console.log(`🔴 Unsubscribed from ${dataType}`);
      return true;
    }

    return false;
  }

  /**
   * Unsubscribe from all data types
   */
  unsubscribeAll() {
    console.log('🔴 Unsubscribing from all real-time updates');

    this.listeners.forEach(({ unsubscribe }) => {
      try {
        unsubscribe();
      } catch (error) {
        console.error('Error unsubscribing:', error);
      }
    });

    this.listeners.clear();
  }

  /**
   * Clean up all subscriptions and listeners
   */
  cleanup() {
    console.log('🧹 Cleaning up real-time data service');

    // Unsubscribe from all listeners
    this.unsubscribeAll();

    // Clear all subscriptions
    this.subscriptions.forEach(unsubscribe => {
      try {
        unsubscribe();
      } catch (error) {
        console.error('Error in subscription cleanup:', error);
      }
    });

    this.subscriptions.clear();

    // Reset state
    this.isInitialized = false;
    this.currentUser = null;
    this.userRole = null;
    this.userPermissions = null;

    console.log('✅ Real-time data service cleaned up');
  }

  /**
   * Get subscription status
   */
  getStatus() {
    return {
      isInitialized: this.isInitialized,
      activeSubscriptions: this.listeners.size,
      subscriptionDetails: Array.from(this.listeners.entries()).map(([key, value]) => ({
        dataType: value.dataType,
        lastUpdate: value.lastUpdate
      })),
      currentUser: this.currentUser?.uid || null
    };
  }
}

// Create singleton instance
const realtimeDataService = new RealtimeDataService();

// Export service
export default realtimeDataService;

// Export class for testing
export { RealtimeDataService };

// Export subscription options for common use cases
export const SUBSCRIPTION_OPTIONS = {
  PROPERTIES: {
    collectionName: 'properties',
    userFilter: true,
    cacheKey: (userId) => CACHE_KEYS.PROPERTIES_ALL(userId),
    refreshInterval: 30000
  },
  TENANTS: {
    collectionName: 'tenants',
    userFilter: true,
    cacheKey: (userId) => CACHE_KEYS.TENANTS_ALL(userId),
    refreshInterval: 30000
  },
  MAINTENANCE: {
    collectionName: 'maintenance',
    userFilter: true,
    cacheKey: (userId) => CACHE_KEYS.MAINTENANCE_ALL(userId),
    refreshInterval: 15000
  },
  PAYMENTS: {
    collectionName: 'payments',
    userFilter: true,
    cacheKey: (userId) => CACHE_KEYS.PAYMENTS_ALL(userId),
    refreshInterval: 30000
  }
};
